import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../context/AdminAuthContext';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import AdminLayout from '../components/AdminLayout';
import AdminFilterBar from '../components/ui/AdminFilterBar';
import AdminPagination from '../components/ui/AdminPagination';
import { AdminLoadingState, AdminEmptyState } from '../components/ui/AdminStates';
import Button from '../components/ui/Button';
import './pageCenso.css';

interface User {
  id_user: number;
  cpf: string;
  special_services: number;
  email: string;
  user_level: string;
}

interface CensoInPerson {
  id_censo_in_person: number;
  id_user: number;
  consultation_date: string;
  time: string;
  token: string;
}

interface CombinedData {
  id_user: number;
  cpf: string;
  special_services: number;
  email: string;
  censo_records: CensoInPerson[];
}

const PAGE_SIZE = 10;
// caminho no /public (com espaço codificado)
const LOGO_PUBLIC_PATH = '/censo%20g2_page-0001.jpg';
// novo limite de tamanho (logo menor)
const LOGO_MAX_WIDTH_MM = 45;  // <<< ajuste aqui se quiser ainda menor
const LOGO_TOP_MARGIN_MM = 10;
const LOGO_AFTER_SPACE_MM = 4;

async function loadPublicImageAsDataURL(publicPath: string): Promise<string> {
  const res = await fetch(publicPath, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`Falha ao carregar imagem: ${publicPath}`);
  const blob = await res.blob();
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

const PageCenso: React.FC = () => {
  const { adminToken, logoutAdmin } = useAdminAuth();
  const navigate = useNavigate();

  const [combinedData, setCombinedData] = useState<CombinedData[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [filter, setFilter] = useState({ startDate: '', endDate: '' });

  const formatBrazilianDate = (dateString: string) => {
    if (!dateString) return '-';
    const clean = dateString.trim();
    if (clean.match(/^\d{4}-\d{2}-\d{2}/)) {
      const [y, m, d] = clean.split(/[ T]/)[0].split('-');
      return `${d}/${m}/${y}`;
    }
    if (clean.includes('/')) return clean;
    if (clean.match(/^\d{2}-\d{2}-\d{4}/)) {
      const [d, m, y] = clean.split('-');
      return `${d}/${m}/${y}`;
    }
    return '-';
  };

  const extractDateString = (dateString: string): string => {
    if (!dateString) return '';
    const clean = dateString.trim();
    if (clean.includes('T')) return clean.split('T')[0];
    if (clean.match(/^\d{4}-\d{2}-\d{2}/)) return clean.split(/[ T]/)[0];
    if (clean.includes('/')) {
      const p = clean.split('/');
      if (p.length === 3) {
        const [d, m, y] = p;
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
      }
    }
    if (clean.match(/^\d{2}-\d{2}-\d{4}/)) {
      const [d, m, y] = clean.split('-');
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return clean;
  };

  const handleGeneratePDF = async () => {
    const filtered = filteredData;
    if (filtered.length === 0) {
      alert('Nenhum dado disponível para gerar o PDF.');
      return;
    }

    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const leftMargin = 20;
      const rightMargin = 20;

      let cursorY = LOGO_TOP_MARGIN_MM;

      // Logo MENOR no topo
      try {
        const logoDataURL = await loadPublicImageAsDataURL(LOGO_PUBLIC_PATH);
        const imgProps = doc.getImageProperties(logoDataURL);

        const logoWidth = Math.min(LOGO_MAX_WIDTH_MM, pageWidth - leftMargin - rightMargin);
        const logoHeight = (imgProps.height / imgProps.width) * logoWidth;
        const logoX = (pageWidth - logoWidth) / 2;

        doc.addImage(logoDataURL, 'JPEG', logoX, cursorY, logoWidth, logoHeight);
        cursorY += logoHeight + LOGO_AFTER_SPACE_MM;
      } catch (e) {
        console.warn('Não foi possível carregar a logo para o PDF:', e);
      }

      // Cabeçalho
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Censo Prev', leftMargin, cursorY);
      cursorY += 7;

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      const periodo =
        filter.startDate || filter.endDate
          ? `Período: ${formatBrazilianDate(filter.startDate) || '—'} a ${formatBrazilianDate(filter.endDate) || '—'}`
          : 'Sem filtro de período';
      doc.text(periodo, leftMargin, cursorY);
      cursorY += 6;

      // Tabela
      autoTable(doc, {
        startY: cursorY,
        head: [['ID User', 'CPF', 'Serviços Especiais', 'Email', 'Data da Consulta', 'Hora', 'Token']],
        body: filtered.flatMap((user) =>
          user.censo_records.map((censo) => [
            user.id_user.toString(),
            user.cpf,
            user.special_services.toString(),
            user.email,
            formatBrazilianDate(censo.consultation_date),
            censo.time,
            censo.token,
          ])
        ),
        styles: { font: 'helvetica', fontSize: 10, cellPadding: 4 },
        headStyles: { fillColor: [25, 85, 191], textColor: [255, 255, 255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        tableWidth: 'auto',
        margin: { top: cursorY, left: leftMargin, right: rightMargin },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 25 },
          2: { cellWidth: 25 },
          3: { cellWidth: 35 },
          4: { cellWidth: 25 },
          5: { cellWidth: 20 },
          6: { cellWidth: 25 },
        },
      });

      const suffix = [filter.startDate || '—', filter.endDate || '—'].join('_');
      doc.save(`relatorio_censo_${suffix}.pdf`);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Falha ao gerar o PDF.');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!adminToken) {
        navigate('/rj/admin-login');
        return;
      }
      setLoading(true);
      try {
        const usersRes = await axios.get<{ users: User[] }>(
          'https://prevcenso.com/backend/api/auth/users',
          { headers: { Authorization: `Bearer ${adminToken}` } }
        );
        const users = usersRes.data.users;

        const censoRes = await axios.get<{ data: CensoInPerson[] }>(
          'https://prevcenso.com/backend/api/censoinperson',
          { headers: { Authorization: `Bearer ${adminToken}` } }
        );
        const censoData = censoRes.data.data;

        const combined = users
          .map((user) => ({
            ...user,
            censo_records: censoData
              .filter((censo) => censo.id_user === user.id_user)
              .sort((a, b) => {
                const da = extractDateString(a.consultation_date);
                const db = extractDateString(b.consultation_date);
                if (da !== db) return da.localeCompare(db);
                return a.time.localeCompare(b.time);
              }),
          }))
          .filter((user) => user.censo_records.length > 0);

        setCombinedData(combined);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        alert('Falha ao carregar dados.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [adminToken, navigate, logoutAdmin]);

  const filteredData: CombinedData[] = combinedData
    .map((user) => {
      let recs = [...user.censo_records];
      const { startDate, endDate } = filter;
      if (startDate || endDate) {
        recs = recs.filter((c) => {
          const d = extractDateString(c.consultation_date);
          if (!d) return false;
          if (startDate && d < startDate) return false;
          if (endDate && d > endDate) return false;
          return true;
        });
      }
      return { ...user, censo_records: recs };
    })
    .filter((user) => user.censo_records.length > 0);

  const sortedFilteredData = [...filteredData].sort((a, b) => {
    const earliestA = a.censo_records[0];
    const earliestB = b.censo_records[0];
    if (!earliestA || !earliestB) return 0;
    const da = extractDateString(earliestA.consultation_date);
    const db = extractDateString(earliestB.consultation_date);
    if (da !== db) return da.localeCompare(db);
    return earliestA.time.localeCompare(earliestB.time);
  });

  const totalPages = Math.ceil(sortedFilteredData.length / PAGE_SIZE) || 1;
  const pagedData = sortedFilteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const chips = [
    (filter.startDate || filter.endDate) && {
      key: 'range',
      label: `Período: ${formatBrazilianDate(filter.startDate) || '—'} a ${formatBrazilianDate(filter.endDate) || '—'}`,
      onRemove: () => setFilter({ startDate: '', endDate: '' }),
    },
  ].filter(Boolean) as { key: string; label: string; onRemove: () => void }[];

  return (
    <AdminLayout title="Censo Presencial">
      <section className="admin-card">
        <div className="admin-card__head">
          <h2>Usuários e Censos Presenciais</h2>
          <Button variant="ghost" onClick={handleGeneratePDF} disabled={filteredData.length === 0}>
            Gerar PDF
          </Button>
        </div>

        <AdminFilterBar chips={chips} resultCount={sortedFilteredData.length} resultLabel="usuários">
          <div className="admin-filter-field">
            <label htmlFor="start-date">Início</label>
            <input
              id="start-date"
              type="date"
              value={filter.startDate}
              onChange={(e) => setFilter((f) => ({ ...f, startDate: e.target.value }))}
              className="admin-input"
            />
          </div>
          <div className="admin-filter-field">
            <label htmlFor="end-date">Fim</label>
            <input
              id="end-date"
              type="date"
              value={filter.endDate}
              onChange={(e) => setFilter((f) => ({ ...f, endDate: e.target.value }))}
              className="admin-input"
            />
          </div>
        </AdminFilterBar>

        {loading ? (
          <AdminLoadingState label="Carregando dados..." />
        ) : pagedData.length === 0 ? (
          <AdminEmptyState message="Nenhum registro encontrado para os filtros aplicados." />
        ) : (
          <>
            <div className="admin-table-wrap">
              <table className="data-table censo-table">
                <thead>
                  <tr>
                    <th>ID User</th>
                    <th>CPF</th>
                    <th>Serviços Especiais</th>
                    <th>Email</th>
                    <th>Data da Consulta</th>
                    <th>Hora</th>
                    <th>Token</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedData.map((item) =>
                    item.censo_records.map((censo, index) => (
                      <tr key={`${item.id_user}-${censo.id_censo_in_person}`} className={index === 0 ? 'censo-table__group-start' : ''}>
                        {index === 0 ? (
                          <>
                            <td rowSpan={item.censo_records.length}>{item.id_user}</td>
                            <td rowSpan={item.censo_records.length}>{item.cpf}</td>
                            <td rowSpan={item.censo_records.length}>{item.special_services}</td>
                            <td rowSpan={item.censo_records.length}>{item.email}</td>
                          </>
                        ) : null}
                        <td>{formatBrazilianDate(censo.consultation_date)}</td>
                        <td>{censo.time}</td>
                        <td>{censo.token}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <AdminPagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </section>
    </AdminLayout>
  );
};

export default PageCenso;
